import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import PageToc from './PageToc';
import LangSwitcher from './LangSwitcher';
import ThemeToggle from './ThemeToggle';
import Search from './Search';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';

interface Props {
  lang: Locale;
  children: ReactNode;
}

const SEPOLIA = {
  EternalStorage:    '0xD81fFB850e44dd131D30E94eeD033CB9c8c546d9',
  MethaToken:        '0x0A1e366572f63bCa45A66dE4F5F1Ab5A62b3E16c',
  OpenGovernance:    '0x7BF8dd021477A44eD0F4b5eB48cB80cfb594917B',
  MethaCrowdsale:    '0x7B96b7e7670C6330b5F4f8b3F46245e4F49FF560',
  AuditingPool:      '0xD301Cff18Cec35F2c698a0fD7DFf81AF81098571',
  AuditorRegistryV2: '0xDead08DfFa69FF1bde30A72E4BD993A67c61Ee03',
};

export default function DocsLayout({ lang, children }: Props) {
  const t = useTranslation();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Lock background scroll while the mobile drawer is open. The cleanup
  // always restores overflow, so the lock can never leak past unmount.
  useEffect(() => {
    if (!navOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  // Behaviours that only apply while the drawer is open:
  //  - Escape closes it,
  //  - growing back past the mobile breakpoint closes it (otherwise the
  //    scroll lock and off-canvas state would strand on desktop, where the
  //    toggle button is hidden and can't undo them),
  //  - focus moves into the drawer on open and back to the toggle on close.
  useEffect(() => {
    if (!navOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    const desktop = window.matchMedia('(min-width: 769px)');
    const onDesktop = (e: MediaQueryListEvent) => {
      if (e.matches) setNavOpen(false);
    };

    document.addEventListener('keydown', onKey);
    desktop.addEventListener('change', onDesktop);
    sidebarRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      desktop.removeEventListener('change', onDesktop);
      menuBtnRef.current?.focus();
    };
  }, [navOpen]);

  return (
    <div className="docs-shell">
      <header className="docs-header">
        <button
          ref={menuBtnRef}
          type="button"
          className="docs-menu-btn"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
          aria-controls="docs-sidebar"
          onClick={() => setNavOpen((v) => !v)}
        >
          <span aria-hidden="true">{navOpen ? '✕' : '☰'}</span>
        </button>
        <Link to="/" className="docs-brand">
          <img src="/favicon.png" alt="" width={20} height={20} />
          <span>{t('site.title')}</span>
        </Link>
        <div className="docs-header-actions">
          <Search compact />
          <a className="docs-header-link" href="https://sepolia.metha.life" target="_blank" rel="noreferrer">
            {t('nav.dapp')} ↗
          </a>
          <LangSwitcher lang={lang} />
          <ThemeToggle />
        </div>
      </header>
      <div className="docs-body">
        <div
          className={navOpen ? 'docs-backdrop docs-backdrop-open' : 'docs-backdrop'}
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
        <aside
          ref={sidebarRef}
          id="docs-sidebar"
          tabIndex={-1}
          className={navOpen ? 'docs-sidebar docs-sidebar-open' : 'docs-sidebar'}
        >
          {/* Controls that live in the header on desktop move in here on mobile,
              where the slim header only has room for the menu button, brand,
              and a compact search. */}
          <div className="docs-drawer-tools">
            <a className="docs-drawer-link" href="https://sepolia.metha.life" target="_blank" rel="noreferrer">
              {t('nav.dapp')} ↗
            </a>
            <div className="docs-drawer-controls">
              <LangSwitcher lang={lang} />
              <ThemeToggle />
            </div>
          </div>
          <Sidebar lang={lang} currentPath={location.pathname} />
        </aside>
        <main className="docs-main">
          <article className="docs-article">{children}</article>
        </main>
        <aside className="docs-toc-rail">
          <PageToc />
        </aside>
      </div>
      <footer className="docs-footer">
        <div className="docs-footer-grid">
          <div className="docs-footer-col">
            <p className="docs-footer-h">{t('site.title')}</p>
            <ul>
              <li><a href="https://metha.life" target="_blank" rel="noreferrer">metha.life</a></li>
              <li><a href="https://sepolia.metha.life" target="_blank" rel="noreferrer">sepolia.metha.life — dapp</a></li>
              <li><a href="https://docs.metha.life" target="_blank" rel="noreferrer">docs.metha.life</a></li>
            </ul>
          </div>
          <div className="docs-footer-col">
            <p className="docs-footer-h">{t('community')}</p>
            <ul>
              <li><a href="https://x.com/MBiofund" target="_blank" rel="noreferrer">X (@MBiofund)</a></li>
              <li><a href="mailto:info@metha.life">info@metha.life</a></li>
              <li><a href={`/${lang}/security/reporting`}>[Security] reports</a></li>
            </ul>
          </div>
          <div className="docs-footer-col">
            <p className="docs-footer-h">{t('code')}</p>
            <ul>
              <li><a href="https://github.com/JVMart/metha" target="_blank" rel="noreferrer">GitHub (private at v3 cut)</a></li>
              <li><a href="https://doi.org/10.5281/zenodo.20038868" target="_blank" rel="noreferrer">DVBC Case Study (Zenodo)</a></li>
              <li><a href="https://metha.life/documents/Metha_Whitepaper_2026_EN.pdf" target="_blank" rel="noreferrer">Whitepaper v6.0 (EN)</a></li>
            </ul>
          </div>
          <div className="docs-footer-col">
            <p className="docs-footer-h">{t('onchain')}</p>
            <ul>
              {Object.entries(SEPOLIA).map(([name, addr]) => (
                <li key={name}>
                  <a href={`https://sepolia.etherscan.io/address/${addr}`} target="_blank" rel="noreferrer">
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="docs-footer-disclaimer">{t('disclaimer')}</p>
      </footer>
      <style>{layoutCss}</style>
    </div>
  );
}

const layoutCss = `
.docs-shell { display: flex; flex-direction: column; min-height: 100vh; }

.docs-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: var(--c-bg);
  border-bottom: 1px solid var(--c-border);
}
.docs-brand {
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; font-size: 0.95rem;
  color: var(--c-fg); text-decoration: none;
}
.docs-header-actions { display: flex; align-items: center; gap: 12px; }
.docs-header-link { font-size: 0.9rem; color: var(--c-muted); }
.docs-header-link:hover { color: var(--c-fg); text-decoration: none; }

.docs-body {
  display: grid;
  grid-template-columns: var(--sidebar-w) minmax(0, 1fr) var(--toc-w);
  flex: 1;
}
.docs-sidebar,
.docs-toc-rail {
  overflow-y: auto;
  overscroll-behavior: contain;
  position: sticky;
  top: 53px;
  height: calc(100vh - 53px);
}
.docs-sidebar {
  border-right: 1px solid var(--c-border);
  padding: 28px 12px 28px 24px;
}
/* Focused programmatically when the mobile drawer opens; it isn't a
   keyboard tab stop (tabIndex -1), so no focus ring is wanted. */
.docs-sidebar:focus { outline: none; }
.docs-toc-rail {
  border-left: 1px solid var(--c-border);
  padding: 28px 24px 28px 12px;
}

.docs-main { padding: 28px 28px; }
.docs-article { max-width: 78ch; margin: 0 auto; line-height: 1.65; }

.docs-footer {
  border-top: 1px solid var(--c-border);
  padding: 32px 24px 20px;
  background: var(--c-bg);
}
.docs-footer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto 16px;
}
.docs-footer-col p.docs-footer-h {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 8px;
  color: var(--c-fg);
}
.docs-footer-col ul {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.88rem;
}
.docs-footer-col li { margin: 4px 0; }
.docs-footer-col a {
  color: var(--c-muted);
  text-decoration: none;
}
.docs-footer-col a:hover { color: var(--c-fg); text-decoration: underline; }
.docs-footer-disclaimer {
  max-width: 1100px;
  margin: 0 auto;
  font-size: 0.78rem;
  color: var(--c-muted);
  line-height: 1.5;
  padding-top: 16px;
  border-top: 1px dashed var(--c-border);
}

/* --- Overflow safety: keep wide content from blowing out the layout.
   Tables (.md-table-wrap) and the mermaid/architecture/data-flow SVGs
   already scroll on their own via globals.css; this only guards stray
   inline images/SVGs. --- */
.docs-article img,
.docs-article svg { max-width: 100%; height: auto; }

/* --- Mobile drawer affordances (inert on desktop) --- */
.docs-menu-btn {
  display: none;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  color: var(--c-fg);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
}
.docs-menu-btn:hover { background: var(--c-bg-alt); }
.docs-backdrop { display: none; }

/* Drawer-only tools (language + theme + dapp link). Hidden on desktop,
   where these controls render in the header instead. */
.docs-drawer-tools { display: none; }

/* --- Tablet: drop the right-hand TOC rail --- */
@media (max-width: 1100px) {
  .docs-body { grid-template-columns: var(--sidebar-w) minmax(0, 1fr); }
  .docs-toc-rail { display: none; }
}

/* --- Phone: single column + off-canvas sidebar drawer --- */
@media (max-width: 768px) {
  .docs-body { grid-template-columns: minmax(0, 1fr); }
  .docs-menu-btn { display: inline-flex; }

  /* Slim header: just menu button, brand, and a compact search.
     Height stays ~53px (driven by the 40px menu button) so it lines up
     with the drawer/backdrop top offset below. */
  .docs-header { padding: 6px 12px; gap: 10px; }
  .docs-header-actions { gap: 8px; flex: 0 1 auto; min-width: 0; }

  /* Brand keeps its space; let the search give way instead. */
  .docs-brand { flex: 0 1 auto; min-width: 0; }
  .docs-brand span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Compact search: a small fraction of the viewport instead of 320px. */
  .docs-header-actions .search-compact { width: clamp(110px, 38vw, 190px); }
  .docs-header-actions .search-compact .search-input { padding: 7px 10px; }

  /* These move into the drawer on mobile. */
  .docs-header .docs-header-link,
  .docs-header-actions .lang-switcher,
  .docs-header-actions .theme-toggle { display: none; }

  /* Zoom the article copy up so it reads comfortably on a phone. */
  .docs-main { padding: 20px 16px; }
  .docs-article { font-size: 17px; }

  .docs-sidebar {
    position: fixed;
    top: 53px;
    left: 0;
    z-index: 40;
    width: min(82vw, 320px);
    height: calc(100vh - 53px);
    height: calc(100dvh - 53px);
    background: var(--c-bg);
    border-right: 1px solid var(--c-border);
    padding: 20px 16px;
    transform: translateX(-100%);
    transition: transform 0.22s ease;
  }
  .docs-sidebar-open { transform: translateX(0); }

  /* Drawer tools row: dapp link, then language + theme controls. */
  .docs-drawer-tools {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--c-border);
  }
  .docs-drawer-link { font-size: 0.95rem; color: var(--c-muted); }
  .docs-drawer-link:hover { color: var(--c-fg); }
  .docs-drawer-controls { display: flex; align-items: center; gap: 12px; }

  .docs-backdrop {
    display: block;
    position: fixed;
    top: 53px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.22s ease;
  }
  .docs-backdrop-open { opacity: 1; pointer-events: auto; }

  .docs-footer { padding: 24px 16px 20px; }
  .docs-footer-grid { gap: 20px; }
}

/* --- Small phone: stack the footer, shrink the brand --- */
@media (max-width: 420px) {
  .docs-footer-grid { grid-template-columns: 1fr; }
  .docs-brand span { font-size: 0.9rem; }
}
`;
